// ADR 8.9.26p §3 — the route half of 6.9.26c §2, which the tree never had.
//
// `RootErrorBoundary` is mounted *above* `<RouterProvider>` (`main.tsx`), and in
// a v6 data router the router's own internal boundary catches every error raised
// while rendering a route element. So the boundary that `main.tsx`'s comment
// promised would catch "a throw from any provider or route" only ever covered
// the providers, and a route that threw rendered React Router's developer page
// and reported nothing. This is the `errorElement` that closes that.
//
// **It localises and `RootErrorBoundary` does not, and that asymmetry is the
// decision, not an oversight.** 6.9.26c declines `t()` because the failure it
// exists for is `initI18n` never having resolved — a localised fallback would
// throw inside the handler for a throw. A route error cannot have that cause:
// routes render only after `await initI18n()`. Copying the boundary's markup
// would also ship copy that is false here — it says Lingara could not *start*,
// and a route that threw started fine and failed later.
//
// The report is in an effect rather than in the render body so a re-render
// cannot file the same failure twice; `captureException` is a no-op with no
// Sentry instance, so a visitor who denied crash reporting still gets this
// screen and still sends nothing.

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouteError } from 'react-router-dom'

import { captureException } from '../telemetry/capture'

export function RouteFailure() {
  const { t } = useTranslation()
  const error = useRouteError()

  useEffect(() => {
    // ADR 9.9.26b §2 — the tag is what separates this population from
    // `RootErrorBoundary`'s in Sentry; both report from a boundary near the
    // root, so the stack alone cannot tell them apart. `RootErrorBoundary` is
    // deliberately left untagged: its untagged reports are the pre-existing
    // population, so historical issues stay comparable.
    captureException(error, { boundary: 'route' })
  }, [error])

  return (
    <div
      role="alert"
      className="mx-auto mt-16 max-w-lg px-6 text-center"
      data-testid="route-failure"
    >
      <p className="leading-relaxed">{t('error_unknown')}</p>
    </div>
  )
}
